/* Created by Interactive 63 - https://interactive63.com */
/**
 * Hand-rolled validation and migration for project data loaded from outside the app, such as
 * project files. Validation rebuilds the project field by field, so unknown extra fields are
 * dropped rather than rejected and files from newer minor versions still load.
 */
import { toSceneName } from './defaults';
import {
  clampMediaWidth,
  defaultMediaSettings,
  isHexColour,
  MEDIA_MAX_PLAYS,
  MEDIA_MAX_WIDTH,
  MEDIA_MIN_WIDTH,
} from './media';
import { isPropertyAllowed, isPropertyKey, PROPERTIES } from '../properties/registry';
import type {
  Easing,
  ElementMeta,
  ExportSettings,
  Keyframe,
  MediaSettings,
  MorphValue,
  MotionHandles,
  Project,
  PropertyKey,
  PropertyValue,
  SceneSettings,
  Track,
  TrackRepeat,
  Trigger,
  Vec2,
} from './types';

export type ValidationResult = { ok: true; project: Project } | { ok: false; errors: string[] };

type Dict = Record<string, unknown>;

const MIN_DURATION = 100;
const MAX_DURATION = 120000;

/** Collects error messages while a value is being read. */
class Reader {
  readonly errors: string[] = [];

  /**
   * Records an error message and returns undefined. The type parameter keeps the result from
   * being a bare `undefined`, which the lint rules treat as a void expression at every call site.
   */
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
  fail<T = never>(message: string): T | undefined {
    this.errors.push(message);
    return undefined;
  }

  /** Reads a plain object. */
  object(value: unknown, where: string): Dict | undefined {
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      return value as Dict;
    }
    return this.fail(`${where} must be an object.`);
  }

  /** Reads an array. */
  array(value: unknown, where: string): unknown[] | undefined {
    return Array.isArray(value) ? (value as unknown[]) : this.fail(`${where} must be a list.`);
  }

  /** Reads a string. */
  string(value: unknown, where: string): string | undefined {
    return typeof value === 'string' ? value : this.fail(`${where} must be text.`);
  }

  /** Reads a boolean. */
  boolean(value: unknown, where: string): boolean | undefined {
    return typeof value === 'boolean' ? value : this.fail(`${where} must be true or false.`);
  }

  /** Reads a finite number within an optional range. */
  number(value: unknown, where: string, min = -Infinity, max = Infinity): number | undefined {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
      return this.fail(`${where} must be a number.`);
    }
    if (value < min || value > max) {
      return this.fail(`${where} must be between ${min} and ${max}.`);
    }
    return value;
  }

  /** Reads one of a fixed set of strings. */
  oneOf<T extends string>(value: unknown, options: readonly T[], where: string): T | undefined {
    if (typeof value === 'string' && (options as readonly string[]).includes(value)) {
      return value as T;
    }
    return this.fail(`${where} must be one of: ${options.join(', ')}.`);
  }

  /** Reads a vector of two finite numbers. */
  vec2(value: unknown, where: string): Vec2 | undefined {
    const obj = this.object(value, where);
    if (!obj) return undefined;
    const x = this.number(obj['x'], `${where}.x`);
    const y = this.number(obj['y'], `${where}.y`);
    return x === undefined || y === undefined ? undefined : { x, y };
  }
}

/** Upgrades older project data to the current schema version before validation. */
export function migrateProject(input: unknown): unknown {
  if (typeof input !== 'object' || input === null) return input;
  switch ((input as Dict)['schemaVersion']) {
    case 1:
      return input;
    default:
      // Unknown versions are left for validation to reject with a clear message.
      return input;
  }
}

/** Validates the easing of a keyframe segment. */
function readEasing(r: Reader, value: unknown, where: string): Easing | undefined {
  const obj = r.object(value, where);
  if (!obj) return undefined;
  const type = r.oneOf(
    obj['type'],
    ['linear', 'keyword', 'cubic', 'steps', 'spring', 'bounce', 'elastic', 'hold'] as const,
    `${where}.type`,
  );
  switch (type) {
    case 'linear':
    case 'hold':
      return { type };
    case 'keyword': {
      const name = r.oneOf(
        obj['name'],
        ['ease', 'ease-in', 'ease-out', 'ease-in-out'] as const,
        `${where}.name`,
      );
      return name && { type, name };
    }
    case 'cubic': {
      const x1 = r.number(obj['x1'], `${where}.x1`, 0, 1);
      const y1 = r.number(obj['y1'], `${where}.y1`);
      const x2 = r.number(obj['x2'], `${where}.x2`, 0, 1);
      const y2 = r.number(obj['y2'], `${where}.y2`);
      return x1 === undefined || y1 === undefined || x2 === undefined || y2 === undefined
        ? undefined
        : { type, x1, y1, x2, y2 };
    }
    case 'steps': {
      const count = r.number(obj['count'], `${where}.count`, 1);
      const position = r.oneOf(
        obj['position'],
        ['jump-start', 'jump-end', 'jump-none', 'jump-both'] as const,
        `${where}.position`,
      );
      return count === undefined || !position ? undefined : { type, count, position };
    }
    case 'spring': {
      const stiffness = r.number(obj['stiffness'], `${where}.stiffness`, 0);
      const damping = r.number(obj['damping'], `${where}.damping`, 0);
      const mass = r.number(obj['mass'], `${where}.mass`, 0);
      return stiffness === undefined || damping === undefined || mass === undefined
        ? undefined
        : { type, stiffness, damping, mass };
    }
    case 'bounce': {
      const bounces = r.number(obj['bounces'], `${where}.bounces`, 0);
      const decay = r.number(obj['decay'], `${where}.decay`, 0);
      return bounces === undefined || decay === undefined ? undefined : { type, bounces, decay };
    }
    case 'elastic': {
      const oscillations = r.number(obj['oscillations'], `${where}.oscillations`, 0);
      const decay = r.number(obj['decay'], `${where}.decay`, 0);
      return oscillations === undefined || decay === undefined
        ? undefined
        : { type, oscillations, decay };
    }
    default:
      return undefined;
  }
}

/** Validates a keyframe value against the value type of its property. */
function readValue(
  r: Reader,
  value: unknown,
  property: PropertyKey,
  where: string,
): PropertyValue | undefined {
  switch (PROPERTIES[property].valueType) {
    case 'number':
      return r.number(value, where);
    case 'colour':
    case 'text':
      return r.string(value, where);
    case 'vec2':
      return r.vec2(value, where);
    case 'draw': {
      const obj = r.object(value, where);
      if (!obj) return undefined;
      const start = r.number(obj['start'], `${where}.start`, 0, 100);
      const end = r.number(obj['end'], `${where}.end`, 0, 100);
      return start === undefined || end === undefined ? undefined : { start, end };
    }
    case 'morph': {
      const obj = r.object(value, where);
      const d = obj && r.string(obj['d'], `${where}.d`);
      if (d === undefined) return undefined;
      const extras = readMorphExtras(r, obj ?? {}, where);
      const alignRaw = obj?.['align'];
      if (alignRaw === undefined) return { d, ...extras };
      const align = r.object(alignRaw, `${where}.align`);
      const shift = align && r.number(align['shift'], `${where}.align.shift`);
      const reverse = align && r.boolean(align['reverse'], `${where}.align.reverse`);
      if (shift === undefined || reverse === undefined) return undefined;
      return { d, align: { shift: Math.round(shift), reverse }, ...extras };
    }
  }
}

/** Validates the optional pins, parts and placement of a morph shape. Invalid ones are dropped. */
function readMorphExtras(r: Reader, obj: Dict, where: string): Partial<MorphValue> {
  const extras: Partial<MorphValue> = {};
  const isIndex = (v: unknown): v is number =>
    typeof v === 'number' && Number.isInteger(v) && v >= 0;
  const pins = obj['pins'];
  if (pins !== undefined) {
    const list = r.array(pins, `${where}.pins`);
    const valid = (list ?? []).filter(
      (p): p is [number, number] =>
        Array.isArray(p) && p.length === 2 && isIndex(p[0]) && isIndex(p[1]),
    );
    if (valid.length > 0) extras.pins = valid.map(([a, b]) => [a, b]);
  }
  const parts = obj['parts'];
  if (parts !== undefined) {
    const list = r.array(parts, `${where}.parts`);
    if (list) extras.parts = list.map((p) => (isIndex(p) ? p : null));
  }
  const placement = obj['placement'];
  if (placement === 'world' || placement === 'in-place') extras.placement = placement;
  const sourceId = obj['sourceId'];
  if (typeof sourceId === 'string') extras.sourceId = sourceId;
  return extras;
}

/** Validates the trigger settings of a scene. */
function readTrigger(r: Reader, value: unknown, where: string): Trigger | undefined {
  const obj = r.object(value, where);
  if (!obj) return undefined;
  const type = r.oneOf(
    obj['type'],
    ['auto', 'hover', 'click', 'inView', 'scroll'] as const,
    `${where}.type`,
  );
  switch (type) {
    case 'auto':
      return { type };
    case 'hover': {
      const mode = r.oneOf(
        obj['mode'],
        ['play-reverse', 'restart', 'pause-resume'] as const,
        `${where}.mode`,
      );
      return mode && { type, mode };
    }
    case 'click': {
      const mode = r.oneOf(
        obj['mode'],
        ['toggle', 'restart', 'play-once'] as const,
        `${where}.mode`,
      );
      return mode && { type, mode };
    }
    case 'inView': {
      const threshold = r.number(obj['threshold'], `${where}.threshold`, 0, 1);
      const once = r.boolean(obj['once'], `${where}.once`);
      const onLeave = r.oneOf(
        obj['onLeave'],
        ['pause', 'reset', 'continue'] as const,
        `${where}.onLeave`,
      );
      return threshold === undefined || once === undefined || !onLeave
        ? undefined
        : { type, threshold, once, onLeave };
    }
    case 'scroll': {
      const startOffset = r.number(obj['startOffset'], `${where}.startOffset`);
      const endOffset = r.number(obj['endOffset'], `${where}.endOffset`);
      const smoothing = r.number(obj['smoothing'], `${where}.smoothing`, 0);
      return startOffset === undefined || endOffset === undefined || smoothing === undefined
        ? undefined
        : { type, startOffset, endOffset, smoothing };
    }
    default:
      return undefined;
  }
}

/** Validates the scene settings. */
function readScene(r: Reader, value: unknown): SceneSettings | undefined {
  const obj = r.object(value, 'scene');
  if (!obj) return undefined;
  const duration = r.number(obj['duration'], 'scene.duration', MIN_DURATION, MAX_DURATION);
  const delay = r.number(obj['delay'], 'scene.delay', 0);
  const rawIterations = obj['iterations'];
  const iterations =
    rawIterations === 'infinite'
      ? 'infinite'
      : r.number(rawIterations, 'scene.iterations (a number or "infinite")', 0);
  const direction = r.oneOf(
    obj['direction'],
    ['normal', 'reverse', 'alternate', 'alternate-reverse'] as const,
    'scene.direction',
  );
  const fillMode = r.oneOf(
    obj['fillMode'],
    ['none', 'forwards', 'backwards', 'both'] as const,
    'scene.fillMode',
  );
  const trigger = readTrigger(r, obj['trigger'], 'scene.trigger');
  const reducedMotion = r.oneOf(
    obj['reducedMotion'],
    ['final-frame', 'first-frame', 'ignore'] as const,
    'scene.reducedMotion',
  );
  if (
    duration === undefined ||
    delay === undefined ||
    iterations === undefined ||
    !direction ||
    !fillMode ||
    !trigger ||
    !reducedMotion
  ) {
    return undefined;
  }
  return { duration, delay, iterations, direction, fillMode, trigger, reducedMotion };
}

/** Validates the optional artboard background, a "#rrggbb" colour. */
function readBackground(r: Reader, value: unknown): string | undefined {
  if (value === undefined) return undefined;
  const text = r.string(value, 'svg.background');
  if (text === undefined) return undefined;
  if (!isHexColour(text)) return r.fail('svg.background must be a colour such as #ffffff.');
  return text.toLowerCase();
}

/** Validates the GIF and MP4 settings. Files saved before they existed get the defaults. */
function readMedia(r: Reader, value: unknown): MediaSettings | undefined {
  if (value === undefined) return defaultMediaSettings();
  const obj = r.object(value, 'export.media');
  if (!obj) return undefined;
  const width = r.number(obj['width'], 'export.media.width', MEDIA_MIN_WIDTH, MEDIA_MAX_WIDTH);
  const fps = r.number(obj['fps'], 'export.media.fps', 1, 120);
  const loop = r.boolean(obj['loop'], 'export.media.loop');
  const plays = r.number(obj['plays'], 'export.media.plays', 1, MEDIA_MAX_PLAYS);
  if (width === undefined || fps === undefined || loop === undefined || plays === undefined) {
    return undefined;
  }
  return {
    width: clampMediaWidth(width),
    fps: Math.round(fps),
    loop,
    plays: Math.round(plays),
  };
}

/** Validates the export settings. */
function readExport(r: Reader, value: unknown): ExportSettings | undefined {
  const obj = r.object(value, 'export');
  if (!obj) return undefined;
  const codeType = r.oneOf(obj['codeType'], ['css', 'css-js'] as const, 'export.codeType');
  const format = r.oneOf(
    obj['format'],
    ['html-document', 'embed-snippet', 'animated-svg', 'zip', 'gif', 'mp4'] as const,
    'export.format',
  );
  const runtimeDelivery = r.oneOf(
    obj['runtimeDelivery'],
    ['inline', 'external-file'] as const,
    'export.runtimeDelivery',
  );
  const media = readMedia(r, obj['media']);
  const sceneName = r.string(obj['sceneName'], 'export.sceneName');
  const prefixIds = r.boolean(obj['prefixIds'], 'export.prefixIds');
  const minify = r.boolean(obj['minify'], 'export.minify');
  const responsive = r.boolean(obj['responsive'], 'export.responsive');
  const accessibleTitle = r.string(obj['accessibleTitle'], 'export.accessibleTitle');
  if (
    !codeType ||
    !format ||
    !runtimeDelivery ||
    !media ||
    sceneName === undefined ||
    prefixIds === undefined ||
    minify === undefined ||
    responsive === undefined ||
    accessibleTitle === undefined
  ) {
    return undefined;
  }
  return {
    codeType,
    format,
    media,
    runtimeDelivery,
    sceneName: toSceneName(sceneName),
    prefixIds,
    minify,
    responsive,
    accessibleTitle,
  };
}

/** Validates the element table. */
function readElements(r: Reader, value: unknown): Record<string, ElementMeta> | undefined {
  const obj = r.object(value, 'elements');
  if (!obj) return undefined;
  const elements: Record<string, ElementMeta> = {};
  for (const [id, raw] of Object.entries(obj)) {
    const where = `Element "${id}"`;
    const el = r.object(raw, where);
    if (!el) continue;
    if (el['id'] !== id) {
      r.fail(`${where} has a mismatched id.`);
      continue;
    }
    const tag = r.string(el['tag'], `${where} tag`);
    const label = r.string(el['label'], `${where} label`);
    const origin = r.vec2(el['origin'], `${where} origin`);
    const excludeFromExport = r.boolean(el['excludeFromExport'], `${where} excludeFromExport`);
    const hiddenInEditor = r.boolean(el['hiddenInEditor'], `${where} hiddenInEditor`);
    const parentId = el['parentId'] === null ? null : r.string(el['parentId'], `${where} parentId`);
    const childList = r.array(el['childIds'], `${where} childIds`);
    const childIds = childList?.filter((c): c is string => typeof c === 'string');
    if (childList && childIds?.length !== childList.length) {
      r.fail(`${where} childIds must all be text.`);
    }
    let pathLength: number | undefined;
    if (el['pathLength'] !== undefined)
      pathLength = r.number(el['pathLength'], `${where} pathLength`, 0);
    if (
      tag === undefined ||
      label === undefined ||
      !origin ||
      excludeFromExport === undefined ||
      hiddenInEditor === undefined ||
      parentId === undefined ||
      !childIds
    ) {
      continue;
    }
    elements[id] = {
      id,
      tag: tag.toLowerCase(),
      label,
      parentId,
      childIds,
      origin,
      excludeFromExport,
      hiddenInEditor,
      ...(pathLength === undefined ? {} : { pathLength }),
    };
  }
  return elements;
}

/** Checks that element references (parents, children, roots) point at existing elements. */
function checkElementReferences(
  r: Reader,
  elements: Record<string, ElementMeta>,
  rootIds: string[],
): void {
  for (const el of Object.values(elements)) {
    if (el.parentId !== null && !(el.parentId in elements)) {
      r.fail(`Element "${el.id}" refers to parent "${el.parentId}", which does not exist.`);
    }
    for (const child of el.childIds) {
      if (!(child in elements)) {
        r.fail(`Element "${el.id}" refers to child "${child}", which does not exist.`);
      }
    }
  }
  for (const id of rootIds) {
    if (!(id in elements)) r.fail(`Root element "${id}" does not exist.`);
  }
}

/** Reads a string that may be absent. */
function optionalString(r: Reader, value: unknown, where: string): string | undefined {
  return value === undefined ? undefined : r.string(value, where);
}

/** Validates one keyframe, enforcing its value type and time range. */
function readKeyframe(
  r: Reader,
  value: unknown,
  trackId: string,
  property: PropertyKey,
  duration: number,
): Keyframe | undefined {
  const where = `Track "${trackId}" keyframe`;
  const obj = r.object(value, where);
  if (!obj) return undefined;
  const id = r.string(obj['id'], `${where} id`);
  const time = r.number(obj['time'], `${where} time`);
  if (time !== undefined && (!Number.isInteger(time) || time < 0 || time > duration)) {
    r.fail(
      `Track "${trackId}" has a keyframe at ${time} ms, outside the scene duration of ${duration} ms.`,
    );
    return undefined;
  }
  const kfValue = readValue(r, obj['value'], property, `${where} value`);
  const easing = readEasing(r, obj['easing'], `${where} easing`);
  const motion = obj['motion'] === undefined ? undefined : readMotion(r, obj['motion'], where);
  return id === undefined || time === undefined || kfValue === undefined || !easing
    ? undefined
    : { id, time, value: kfValue, easing, ...(motion ? { motion } : {}) };
}

/** Validates the curve handles of a position keyframe. */
function readMotion(r: Reader, value: unknown, where: string): MotionHandles | undefined {
  const obj = r.object(value, `${where} motion`);
  if (!obj) return undefined;
  const handleIn = obj['in'] === undefined ? undefined : r.vec2(obj['in'], `${where} motion.in`);
  const handleOut =
    obj['out'] === undefined ? undefined : r.vec2(obj['out'], `${where} motion.out`);
  const corner =
    obj['corner'] === undefined ? undefined : r.boolean(obj['corner'], `${where} motion.corner`);
  return {
    ...(handleIn ? { in: handleIn } : {}),
    ...(handleOut ? { out: handleOut } : {}),
    ...(corner ? { corner } : {}),
  };
}

/** Validates a track's optional repeat settings against its keyframes. */
function readRepeat(
  r: Reader,
  value: unknown,
  where: string,
  keyframes: readonly Keyframe[],
): TrackRepeat | undefined {
  if (value === undefined) return undefined;
  const obj = r.object(value, `${where} repeat`);
  if (!obj) return undefined;
  const rawPeriod = obj['period'];
  const period = rawPeriod === null ? null : r.number(rawPeriod, `${where} repeat.period`, 100);
  const rawCount = obj['count'];
  let count: number | 'infinite' | undefined;
  if (rawCount === 'infinite') count = 'infinite';
  else if (
    typeof rawCount === 'number' &&
    Number.isInteger(rawCount) &&
    rawCount >= 2 &&
    rawCount <= 1000
  ) {
    count = rawCount;
  } else {
    r.fail(`${where} repeat.count must be a whole number from 2 to 1000, or "infinite".`);
  }
  const alternate = r.boolean(obj['alternate'], `${where} repeat.alternate`);
  const lastTime = keyframes[keyframes.length - 1]?.time ?? 0;
  if (typeof period === 'number' && period < lastTime) {
    r.fail(`${where} has a repeat cycle shorter than its last keyframe.`);
  }
  if (period === undefined || count === undefined || alternate === undefined) return undefined;
  return { period, count, alternate };
}

/** Validates the track list against the elements and scene duration. */
function readTracks(
  r: Reader,
  value: unknown,
  elements: Record<string, ElementMeta>,
  duration: number,
): Track[] | undefined {
  const list = r.array(value, 'tracks');
  if (!list) return undefined;
  const tracks: Track[] = [];
  const seen = new Set<string>();
  for (const raw of list) {
    const obj = r.object(raw, 'Each track');
    if (!obj) continue;
    const id = r.string(obj['id'], 'Track id');
    if (id === undefined) continue;
    const where = `Track "${id}"`;
    const elementId = r.string(obj['elementId'], `${where} elementId`);
    const rawProperty = r.string(obj['property'], `${where} property`);
    const enabled = r.boolean(obj['enabled'], `${where} enabled`);
    const rawKeyframes = r.array(obj['keyframes'], `${where} keyframes`);
    if (elementId === undefined || rawProperty === undefined) continue;
    const element = elements[elementId];
    if (!element) {
      r.fail(`${where} refers to element "${elementId}", which does not exist.`);
      continue;
    }
    if (!isPropertyKey(rawProperty)) {
      r.fail(`${where} animates unknown property "${rawProperty}".`);
      continue;
    }
    if (!isPropertyAllowed(rawProperty, element.tag)) {
      r.fail(
        `${where} animates "${rawProperty}", which is not available on <${element.tag}> elements.`,
      );
    }
    const pairKey = `${elementId}:${rawProperty}`;
    if (seen.has(pairKey)) {
      r.fail(`Element "${elementId}" has more than one track for "${rawProperty}".`);
    }
    seen.add(pairKey);

    const keyframes: Keyframe[] = [];
    let previous = -1;
    for (const rawKf of rawKeyframes ?? []) {
      const kf = readKeyframe(r, rawKf, id, rawProperty, duration);
      if (!kf) continue;
      if (kf.time === previous) {
        r.fail(`${where} has two keyframes at ${kf.time} ms.`);
      } else if (kf.time < previous) {
        r.fail(`${where} has keyframes out of order: ${kf.time} ms comes after ${previous} ms.`);
      }
      previous = Math.max(previous, kf.time);
      keyframes.push(kf);
    }
    const repeat = readRepeat(r, obj['repeat'], where, keyframes);
    const restart = obj['restartWithScene'];
    if (restart !== undefined && typeof restart !== 'boolean') {
      r.fail(`${where} restartWithScene must be true or false.`);
    }
    const orient = obj['orient'];
    if (orient !== undefined && typeof orient !== 'boolean') {
      r.fail(`${where} orient must be true or false.`);
    }
    if (enabled !== undefined) {
      tracks.push({
        id,
        elementId,
        property: rawProperty,
        keyframes,
        enabled,
        ...(repeat ? { repeat } : {}),
        ...(typeof restart === 'boolean' ? { restartWithScene: restart } : {}),
        ...(orient === true ? { orient: true } : {}),
      });
    }
  }
  for (const track of tracks) {
    if (
      track.property === 'draw' &&
      tracks.some((t) => t.elementId === track.elementId && t.property === 'morph')
    ) {
      r.fail(`Element "${track.elementId}" cannot have both "draw" and "morph" tracks.`);
    }
  }
  return tracks;
}

/**
 * Validates untrusted project data. On success the returned project contains only known fields;
 * on failure every problem found is listed in human-readable form.
 */
export function validateProject(input: unknown): ValidationResult {
  const r = new Reader();
  const root = r.object(migrateProject(input), 'Project data');
  if (!root) return { ok: false, errors: r.errors };

  if (root['schemaVersion'] !== 1) {
    const found = typeof root['schemaVersion'] === 'number' ? ` ${root['schemaVersion']}` : '';
    return { ok: false, errors: [`Unsupported schemaVersion${found}. Expected 1.`] };
  }

  const id = r.string(root['id'], 'id');
  const name = r.string(root['name'], 'name');
  const createdAt = r.string(root['createdAt'], 'createdAt');
  const updatedAt = r.string(root['updatedAt'], 'updatedAt');

  const svg = r.object(root['svg'], 'svg');
  const source = svg && r.string(svg['source'], 'svg.source');
  const fileName = svg && r.string(svg['fileName'], 'svg.fileName');
  const originalWidth = optionalString(r, svg?.['originalWidth'], 'svg.originalWidth');
  const originalHeight = optionalString(r, svg?.['originalHeight'], 'svg.originalHeight');
  const background = readBackground(r, svg?.['background']);
  const vb = svg && r.object(svg['viewBox'], 'svg.viewBox');
  const vbX = vb && r.number(vb['x'], 'svg.viewBox.x');
  const vbY = vb && r.number(vb['y'], 'svg.viewBox.y');
  const vbWidth = vb && r.number(vb['width'], 'svg.viewBox.width');
  const vbHeight = vb && r.number(vb['height'], 'svg.viewBox.height');
  if (vbWidth !== undefined && vbWidth <= 0) r.fail('svg.viewBox.width must be greater than 0.');
  if (vbHeight !== undefined && vbHeight <= 0) r.fail('svg.viewBox.height must be greater than 0.');

  const elements = readElements(r, root['elements']);
  const rootList = r.array(root['rootIds'], 'rootIds');
  const rootIds = rootList?.filter((v): v is string => typeof v === 'string');
  if (rootList && rootIds?.length !== rootList.length) r.fail('rootIds must all be text.');
  if (elements && rootIds) checkElementReferences(r, elements, rootIds);

  const scene = readScene(r, root['scene']);
  const exportSettings = readExport(r, root['export']);
  const tracks =
    elements && scene ? readTracks(r, root['tracks'], elements, scene.duration) : undefined;

  if (
    r.errors.length > 0 ||
    id === undefined ||
    name === undefined ||
    createdAt === undefined ||
    updatedAt === undefined ||
    source === undefined ||
    fileName === undefined ||
    vbX === undefined ||
    vbY === undefined ||
    vbWidth === undefined ||
    vbHeight === undefined ||
    !elements ||
    !rootIds ||
    !scene ||
    !exportSettings ||
    !tracks
  ) {
    return { ok: false, errors: r.errors.length > 0 ? r.errors : ['Project data is incomplete.'] };
  }

  return {
    ok: true,
    project: {
      schemaVersion: 1,
      id,
      name,
      createdAt,
      updatedAt,
      svg: {
        source,
        viewBox: { x: vbX, y: vbY, width: vbWidth, height: vbHeight },
        fileName,
        ...(originalWidth === undefined ? {} : { originalWidth }),
        ...(originalHeight === undefined ? {} : { originalHeight }),
        ...(background === undefined ? {} : { background }),
      },
      elements,
      rootIds,
      tracks,
      scene,
      export: exportSettings,
    },
  };
}
